import "./PaymentHistoryModal.scss";

import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import Card from "components/Card/Card";
import { DatePicker, MaskedInput, TextInput } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import type { PaymentHistory } from "pages/liens/LienTypes";
import { useEffect, useState } from "react";
import type { FieldErrors } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import type { CodeType } from "./CodeSection";
import CodeSection from "./CodeSection";

export type PaymentHistoryFormData = {
  icn: string;
  provider_name: string;
  level_of_appeal: string;
  diagnosis_codes: CodeType[];
  hcpcs_codes: CodeType[];
  cpt_codes: CodeType[];
  drg_codes: CodeType[];
  ndc_codes: CodeType[];
  correct_all: CorrectAllType;
  from_date: string;
  to_date: string;
  conditional_payment: string;
};

type CorrectAllType = {
  diagnosis_codes: { before: CodeType; after: CodeType }[];
  hcpcs_codes: { before: CodeType; after: CodeType }[];
  cpt_codes: { before: CodeType; after: CodeType }[];
  drg_codes: { before: CodeType; after: CodeType }[];
  ndc_codes: { before: CodeType; after: CodeType }[];
};

type PaymentHistoryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: PaymentHistoryFormData) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  initialData?: PaymentHistory;
  title: string;
  levelOfAppeal: string;
  partType: string;
};

const FormErrors = ({
  errors,
}: {
  errors: FieldErrors<PaymentHistoryFormData>;
}) => {
  if (Object.keys(errors).length === 0) return null;

  return (
    <div className="error-summary">
      {Object.entries(errors).map(([field, error]) => (
        <div key={field} className="error-message">
          {error?.message || `${field} is invalid`}
        </div>
      ))}
    </div>
  );
};

const PaymentHistoryModal = ({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  initialData,
  title,
  levelOfAppeal,
  partType,
}: PaymentHistoryModalProps) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [diagnosisCodes, setDiagnosisCodes] = useState<CodeType[]>([]);
  const [cptCodes, setCptCodes] = useState<CodeType[]>([]);
  const [drgCodes, setDrgCodes] = useState<CodeType[]>([]);
  const [hcpcsCodes, setHcpcsCodes] = useState<CodeType[]>([]);
  const [ndcCodes, setNdcCodes] = useState<CodeType[]>([]);
  const [correctAll, setCorrectAll] = useState({
    diagnosis_codes: [],
    hcpcs_codes: [],
    cpt_codes: [],
    drg_codes: [],
    ndc_codes: [],
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<PaymentHistoryFormData>({
    defaultValues: {
      icn: "",
      provider_name: "",
      level_of_appeal: "",
      diagnosis_codes: [],
      hcpcs_codes: [],
      cpt_codes: [],
      drg_codes: [],
      ndc_codes: [],
      correct_all: {
        diagnosis_codes: [],
        hcpcs_codes: [],
        cpt_codes: [],
        drg_codes: [],
        ndc_codes: [],
      },
      from_date: "",
      to_date: "",
      conditional_payment: "",
    },
    mode: "onBlur",
    shouldFocusError: false,
  });

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        const diagnosisCodesData =
          initialData.diagnosis_codes_with_descriptions;
        const cptCodesData =
          initialData.cpt_codes_with_descriptions ||
          initialData.cpt.map((code) => ({ code, description: "" }));
        const drgCodesData =
          initialData.drg_codes_with_descriptions ||
          initialData.drg.map((code) => ({ code, description: "" }));
        const hcpcsCodesData =
          initialData.hcpcs_codes_with_descriptions ||
          initialData.hcpcs.map((code) => ({ code, description: "" }));
        const ndcCodesData =
          initialData.ndc_codes_with_descriptions ||
          (initialData.ndc || []).map((code) => ({ code, description: "" }));

        setDiagnosisCodes(diagnosisCodesData as CodeType[]);
        setCptCodes(cptCodesData);
        setDrgCodes(drgCodesData);
        setHcpcsCodes(hcpcsCodesData);
        setNdcCodes(ndcCodesData);

        reset({
          icn: initialData.icn,
          provider_name: initialData.provider_name,
          level_of_appeal: initialData.level_of_appeal,
          diagnosis_codes: diagnosisCodesData as CodeType[],
          hcpcs_codes: hcpcsCodesData,
          cpt_codes: cptCodesData,
          drg_codes: drgCodesData,
          ndc_codes: ndcCodesData,
          from_date: initialData.from_date,
          to_date: initialData.to_date,
          conditional_payment: initialData.conditional_payment.toString(),
        });
      } else {
        setDiagnosisCodes([]);
        setCptCodes([]);
        setDrgCodes([]);
        setHcpcsCodes([]);
        setNdcCodes([]);
        reset({
          icn: "",
          provider_name: "",
          level_of_appeal: "",
          diagnosis_codes: [],
          hcpcs_codes: [],
          cpt_codes: [],
          drg_codes: [],
          ndc_codes: [],
          from_date: "",
          to_date: "",
          conditional_payment: "",
        });
      }
    }
  }, [isOpen, initialData, reset]);

  const handleFormSubmit = handleSubmit(async (data) => {
    try {
      await onSubmit({
        ...data,
        diagnosis_codes: diagnosisCodes,
        cpt_codes: cptCodes,
        drg_codes: drgCodes,
        hcpcs_codes: hcpcsCodes,
        ndc_codes: ndcCodes,
        correct_all: correctAll,
        level_of_appeal: levelOfAppeal,
      });

      setCorrectAll({
        diagnosis_codes: [],
        hcpcs_codes: [],
        cpt_codes: [],
        drg_codes: [],
        ndc_codes: [],
      });
      onClose();
    } catch (error) {
      console.error("Form submission error:", error);
    }
  });

  const handleDelete = async () => {
    try {
      if (onDelete && initialData) {
        await onDelete(initialData.id.toString());
        setShowDeleteConfirm(false);
        onClose();
      }
    } catch (error) {
      console.error("Delete error:", error);
    }
  };

  const handleAddDiagnosisCode = () => {
    setDiagnosisCodes([
      ...diagnosisCodes,
      { code: "", description: "", indicator: "0", isNew: true },
    ]);
  };

  const handleRemoveDiagnosisCode = (index: number) => {
    const updatedCodes = [...diagnosisCodes];
    const removedCode = updatedCodes.splice(index, 1)[0];
    setDiagnosisCodes(updatedCodes);

    setCorrectAll((prev) => ({
      ...prev,
      diagnosis_codes: prev.diagnosis_codes.filter(
        (entry) => entry.before.code !== removedCode.code,
      ),
    }));
  };

  const handleDiagnosisCodeChange = (
    index: number,
    updates: Partial<CodeType>,
  ) => {
    const updatedCodes = diagnosisCodes.map((code, i) =>
      i === index ? { ...code, ...updates } : code,
    );
    setDiagnosisCodes(updatedCodes);

    const oldCode = initialData?.diagnosis_codes_with_descriptions[index] || {
      code: "",
      description: "",
      isNew: false,
    };
    setCorrectAll((prev) => ({
      ...prev,
      diagnosis_codes: prev.diagnosis_codes.filter(
        (entry) => entry.before.code !== oldCode.code,
      ),
    }));
  };

  const handleAddCptCode = () => {
    setCptCodes([...cptCodes, { code: "", description: "", isNew: true }]);
  };

  const handleRemoveCptCode = (index: number) => {
    const updatedCodes = [...cptCodes];
    const removedCode = updatedCodes.splice(index, 1)[0];
    setCptCodes(updatedCodes);

    setCorrectAll((prev) => ({
      ...prev,
      cpt_codes: prev.cpt_codes.filter(
        (entry) => entry.before.code !== removedCode.code,
      ),
    }));
  };

  const handleCptCodeChange = (index: number, updates: Partial<CodeType>) => {
    const updatedCodes = cptCodes.map((code, i) =>
      i === index ? { ...code, ...updates } : code,
    );
    setCptCodes(updatedCodes);

    const oldCode = initialData?.cpt_codes_with_descriptions[index] || {
      code: "",
      description: "",
      isNew: false,
    };
    setCorrectAll((prev) => ({
      ...prev,
      cpt_codes: prev.cpt_codes.filter(
        (entry) => entry.before.code !== oldCode.code,
      ),
    }));
  };

  const handleAddDrgCode = () => {
    setDrgCodes([...drgCodes, { code: "", description: "", isNew: true }]);
  };

  const handleRemoveDrgCode = (index: number) => {
    const updatedCodes = [...drgCodes];
    const removedCode = updatedCodes.splice(index, 1)[0];
    setDrgCodes(updatedCodes);

    setCorrectAll((prev) => ({
      ...prev,
      drg_codes: prev.drg_codes.filter(
        (entry) => entry.before.code !== removedCode.code,
      ),
    }));
  };

  const handleDrgCodeChange = (index: number, updates: Partial<CodeType>) => {
    const updatedCodes = drgCodes.map((code, i) =>
      i === index ? { ...code, ...updates } : code,
    );
    setDrgCodes(updatedCodes);

    const oldCode = initialData?.drg_codes_with_descriptions[index] || {
      code: "",
      description: "",
      isNew: false,
    };
    setCorrectAll((prev) => ({
      ...prev,
      drg_codes: prev.drg_codes.filter(
        (entry) => entry.before.code !== oldCode.code,
      ),
    }));
  };

  const handleAddHcpcsCode = () => {
    setHcpcsCodes([...hcpcsCodes, { code: "", description: "", isNew: true }]);
  };

  const handleRemoveHcpcsCode = (index: number) => {
    const updatedCodes = [...hcpcsCodes];
    const removedCode = updatedCodes.splice(index, 1)[0];
    setHcpcsCodes(updatedCodes);

    setCorrectAll((prev) => ({
      ...prev,
      hcpcs_codes: prev.hcpcs_codes.filter(
        (entry) => entry.before.code !== removedCode.code,
      ),
    }));
  };

  const handleHcpcsCodeChange = (index: number, updates: Partial<CodeType>) => {
    const updatedCodes = hcpcsCodes.map((code, i) =>
      i === index ? { ...code, ...updates } : code,
    );
    setHcpcsCodes(updatedCodes);

    const oldCode = initialData?.hcpcs_codes_with_descriptions[index] || {
      code: "",
      description: "",
      isNew: false,
    };
    setCorrectAll((prev) => ({
      ...prev,
      hcpcs_codes: prev.hcpcs_codes.filter(
        (entry) => entry.before.code !== oldCode.code,
      ),
    }));
  };

  const handleAddNdcCode = () => {
    setNdcCodes([...ndcCodes, { code: "", description: "", isNew: true }]);
  };

  const handleRemoveNdcCode = (index: number) => {
    const updatedCodes = [...ndcCodes];
    const removedCode = updatedCodes.splice(index, 1)[0];
    setNdcCodes(updatedCodes);

    setCorrectAll((prev) => ({
      ...prev,
      ndc_codes: prev.ndc_codes.filter(
        (entry) => entry.before.code !== removedCode.code,
      ),
    }));
  };

  const handleNdcCodeChange = (index: number, updates: Partial<CodeType>) => {
    const updatedCodes = ndcCodes.map((code, i) =>
      i === index ? { ...code, ...updates } : code,
    );
    setNdcCodes(updatedCodes);

    const oldCode = initialData?.ndc_codes_with_descriptions?.[index] || {
      code: "",
      description: "",
      isNew: false,
    };
    setCorrectAll((prev) => ({
      ...prev,
      ndc_codes: prev.ndc_codes.filter(
        (entry) => entry.before.code !== oldCode.code,
      ),
    }));
  };

  const handleCorrectAllChange = (
    index: number,
    type: keyof CorrectAllType,
    updates: Partial<CodeType> | null,
  ) => {
    const initial_codes =
      type === "diagnosis_codes"
        ? initialData?.diagnosis_codes_with_descriptions || []
        : type === "hcpcs_codes"
          ? initialData?.hcpcs_codes_with_descriptions || []
          : type === "cpt_codes"
            ? initialData?.cpt_codes_with_descriptions || []
            : type === "drg_codes"
              ? initialData?.drg_codes_with_descriptions || []
              : type === "ndc_codes"
                ? initialData?.ndc_codes_with_descriptions || []
                : [];

    const old_code = initial_codes[index] || {
      code: "",
      description: "",
      indicator: "0",
    };

    setCorrectAll((prev) => {
      if (updates === null) {
        return {
          ...prev,
          [type]: prev[type].filter(
            (entry) =>
              JSON.stringify(entry.before) !== JSON.stringify(old_code),
          ),
        };
      }

      const exists = prev[type].some(
        (entry) =>
          JSON.stringify(entry.before) === JSON.stringify(old_code) &&
          JSON.stringify(entry.after) === JSON.stringify(updates),
      );

      if (exists) {
        return prev;
      }

      return {
        ...prev,
        [type]: [
          ...prev[type],
          {
            before: old_code,
            after: updates,
          },
        ],
      };
    });
  };

  return (
    <>
      <KaboodleModal
        isOpen={isOpen}
        onClose={onClose}
        size="extra-large"
        footer={
          <div className="flex jc-c">
            <Button onClick={handleFormSubmit}>Save</Button>
            <Button onClick={onClose}>Cancel</Button>
          </div>
        }
      >
        <Card
          heading={title}
          headerButton={
            initialData && (
              <ButtonIcon
                kind="neutral"
                size="small"
                onClick={() => setShowDeleteConfirm(true)}
                className="delete"
              >
                delete
              </ButtonIcon>
            )
          }
        >
          <FormErrors errors={errors} />
          <Controller
            name="icn"
            control={control}
            rules={{ required: "ICN is required" }}
            render={({ field }) => (
              <TextInput
                {...field}
                ref={field.ref}
                label="ICN"
                errors={errors}
              />
            )}
          />

          <Controller
            name="provider_name"
            control={control}
            rules={{
              required: `${partType === "D" ? "Prescriber" : "Provider"} name is required`,
            }}
            render={({ field }) => (
              <TextInput
                {...field}
                ref={field.ref}
                label={partType === "D" ? "Prescriber Name" : "Provider Name"}
                errors={errors}
              />
            )}
          />

          <div className="border mrg-bottom"></div>

          {partType === "D" ? (
            // show only NDC codes for Part D liens
            <CodeSection
              codes={ndcCodes}
              type="NDC"
              onAdd={handleAddNdcCode}
              onRemove={handleRemoveNdcCode}
              onChange={handleNdcCodeChange}
              onCorrectAllChange={handleCorrectAllChange}
              showCorrectAll={!!initialData}
            />
          ) : (
            // show ICD, HCPCS, CPT, DRG codes for non-Part D liens
            <>
              <CodeSection
                codes={diagnosisCodes}
                type="ICD"
                onAdd={handleAddDiagnosisCode}
                onRemove={handleRemoveDiagnosisCode}
                onChange={handleDiagnosisCodeChange}
                onCorrectAllChange={handleCorrectAllChange}
                showCorrectAll={!!initialData}
              />

              <div className="border mrg-bottom"></div>

              <CodeSection
                codes={hcpcsCodes}
                type="HCPCS"
                onAdd={handleAddHcpcsCode}
                onRemove={handleRemoveHcpcsCode}
                onChange={handleHcpcsCodeChange}
                onCorrectAllChange={handleCorrectAllChange}
                showCorrectAll={!!initialData}
              />

              <div className="border mrg-bottom"></div>

              <CodeSection
                codes={cptCodes}
                type="CPT"
                onAdd={handleAddCptCode}
                onRemove={handleRemoveCptCode}
                onChange={handleCptCodeChange}
                onCorrectAllChange={handleCorrectAllChange}
                showCorrectAll={!!initialData}
              />

              <div className="border mrg-bottom"></div>

              <CodeSection
                codes={drgCodes}
                type="DRG"
                onAdd={handleAddDrgCode}
                onRemove={handleRemoveDrgCode}
                onChange={handleDrgCodeChange}
                onCorrectAllChange={handleCorrectAllChange}
                showCorrectAll={!!initialData}
              />
            </>
          )}

          <div className="border mrg-bottom"></div>

          <Controller
            name="from_date"
            control={control}
            rules={{
              required: "Date is required",
              validate: (value) => {
                if (!value) return "Date is required";
                const date = new Date(value);
                if (isNaN(date.getTime())) return "Invalid date format";
                if (date > new Date()) return "Date cannot be in the future";
                return true;
              },
            }}
            render={({ field: { onChange, value, ...field } }) => (
              <DatePicker
                {...field}
                initialDate={value ? new Date(value) : undefined}
                onDateChange={(date) => {
                  const dateString = date
                    ? date.toISOString().split("T")[0]
                    : "";
                  onChange(dateString);
                  // For Part D, auto-set to_date to same value since it's required but hidden
                  if (partType === "D") {
                    setValue("to_date", dateString);
                  }
                }}
                label={partType === "D" ? "Date Filled" : "From Date"}
                errors={errors}
              />
            )}
          />
          {partType !== "D" && (
            <Controller
              name="to_date"
              control={control}
              rules={{
                required: "Date is required",
                validate: (value) => {
                  if (!value) return "Date is required";
                  const date = new Date(value);
                  if (isNaN(date.getTime())) return "Invalid date format";
                  if (date > new Date()) return "Date cannot be in the future";
                  return true;
                },
              }}
              render={({ field: { onChange, value, ...field } }) => (
                <DatePicker
                  {...field}
                  initialDate={value ? new Date(value) : undefined}
                  onDateChange={(date) => {
                    onChange(date ? date.toISOString().split("T")[0] : "");
                  }}
                  label="To Date"
                  errors={errors}
                />
              )}
            />
          )}

          <Controller
            name="conditional_payment"
            control={control}
            rules={{ required: "Conditional payment is required" }}
            render={({ field }) => (
              <MaskedInput
                {...field}
                ref={field.ref}
                label="Conditional Payment"
                mask="dollar"
                errors={errors}
              />
            )}
          />
        </Card>
      </KaboodleModal>

      {showDeleteConfirm && (
        <KaboodleModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          footer={
            <div className="flex jc-c">
              <Button className="delete" onClick={handleDelete}>
                Confirm Delete
              </Button>
              <Button onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
            </div>
          }
        >
          <Card heading="Delete Payment History">
            <p>Are you sure you want to delete this payment history item?</p>
          </Card>
        </KaboodleModal>
      )}
    </>
  );
};

export default PaymentHistoryModal;
